import { supabase } from './supabase';

export interface TaxRate {
  id: string;
  business_id: string;
  name: string;
  description: string;
  rate: number;
  is_compound: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export async function getTaxRates(businessId: string) {
  const { data, error } = await supabase
    .from('tax_rates')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at');

  if (error) throw error;
  return data as TaxRate[];
}

export async function createTaxRate(taxRate: Omit<TaxRate, 'id' | 'created_at' | 'updated_at'>) {
  const { data, error } = await supabase
    .from('tax_rates')
    .insert([taxRate])
    .select()
    .single();

  if (error) throw error;
  return data as TaxRate;
}

export async function updateTaxRate(id: string, updates: Partial<TaxRate>) {
  const { data, error } = await supabase
    .from('tax_rates')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as TaxRate;
}

async function deleteTaxRate(id: string) {
  const { error } = await supabase
    .from('tax_rates')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export { deleteTaxRate };

export interface ServiceAdditionalTax {
  id: string;
  service_id: string;
  name: string;
  rate: number;
  created_at: string;
}

export async function getServiceAdditionalTaxes(serviceId: string): Promise<ServiceAdditionalTax[]> {
  const { data, error } = await supabase
    .from('service_additional_taxes')
    .select('*')
    .eq('service_id', serviceId)
    .order('created_at');

  if (error) throw error;
  return data as ServiceAdditionalTax[];
}

export async function createServiceAdditionalTax(
  serviceId: string,
  name: string,
  rate: number
): Promise<ServiceAdditionalTax> {
  const { data, error } = await supabase
    .from('service_additional_taxes')
    .insert([{ service_id: serviceId, name, rate }])
    .select()
    .single();

  if (error) throw error;
  return data as ServiceAdditionalTax;
}

export async function deleteServiceAdditionalTax(id: string): Promise<void> {
  const { error } = await supabase
    .from('service_additional_taxes')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// ── Add-on additional taxes (parity with services) ──────────────
export interface AddonAdditionalTax {
  id: string;
  addon_id: string;
  name: string;
  rate: number;
  created_at: string;
}

export async function getAddonAdditionalTaxes(addonId: string): Promise<AddonAdditionalTax[]> {
  const { data, error } = await supabase
    .from('service_addon_additional_taxes')
    .select('*')
    .eq('addon_id', addonId)
    .order('created_at');

  if (error) throw error;
  return data as AddonAdditionalTax[];
}

export async function createAddonAdditionalTax(
  addonId: string,
  name: string,
  rate: number
): Promise<AddonAdditionalTax> {
  const { data, error } = await supabase
    .from('service_addon_additional_taxes')
    .insert([{ addon_id: addonId, name, rate }])
    .select()
    .single();

  if (error) throw error;
  return data as AddonAdditionalTax;
}

export interface StockItemAdditionalTax {
  id: string;
  stock_item_id: string;
  name: string;
  rate: number;
  created_at: string;
}

export async function getStockItemAdditionalTaxes(stockItemId: string): Promise<StockItemAdditionalTax[]> {
  const { data, error } = await supabase
    .from('stock_item_additional_taxes')
    .select('*')
    .eq('stock_item_id', stockItemId)
    .order('created_at');
  if (error) throw error;
  return data as StockItemAdditionalTax[];
}

export async function createStockItemAdditionalTax(
  stockItemId: string,
  name: string,
  rate: number
): Promise<StockItemAdditionalTax> {
  const { data, error } = await supabase
    .from('stock_item_additional_taxes')
    .insert([{ stock_item_id: stockItemId, name, rate }])
    .select()
    .single();
  if (error) throw error;
  return data as StockItemAdditionalTax;
}

export async function deleteStockItemAdditionalTax(id: string): Promise<void> {
  const { error } = await supabase
    .from('stock_item_additional_taxes')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

export async function deleteAddonAdditionalTax(id: string): Promise<void> {
  const { error } = await supabase
    .from('service_addon_additional_taxes')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export interface AdditionalTaxLine {
  name: string;
  rate: number;
  amount: number;
  /** Pre-tax amount this rate was applied to (after booking discount). */
  taxableBase: number;
}

/**
 * Booking discount as a fraction of subtotal (e.g. $49.40 / $494 ≈ 10%).
 * Taxable share of any line = line_gross * this value — same as line × (1 − discount%).
 */
export function bookingTaxableLineMultiplier(subtotal: number, discountDollars: number): number {
  const s = Number(subtotal) || 0;
  if (s <= 0) return 0;
  const d = Math.max(0, Number(discountDollars) || 0);
  const capped = Math.min(d, s);
  return (s - capped) / s;
}

export async function getBookingAdditionalTaxBreakdown(bookingId: string): Promise<AdditionalTaxLine[]> {
  const { data: bookingRow, error: bErr } = await supabase
    .from('bookings')
    .select('subtotal, discount_amount')
    .eq('id', bookingId)
    .maybeSingle();

  if (bErr || !bookingRow) return [];

  const grossSubtotal = Number(bookingRow.subtotal) || 0;
  const discountAmount = Number(bookingRow.discount_amount) || 0;
  const lineMultiplier = bookingTaxableLineMultiplier(grossSubtotal, discountAmount);

  const { data: bookingServices, error: bsError } = await supabase
    .from('booking_services')
    .select('id, service_id, price')
    .eq('booking_id', bookingId);

  if (bsError || !bookingServices || bookingServices.length === 0) return [];

  const bsIds = bookingServices.map((bs) => bs.id).filter(Boolean);
  const addonByBsId = new Map<string, number>();
  if (bsIds.length > 0) {
    const { data: addonRows } = await supabase
      .from('booking_service_addons')
      .select('booking_service_id, price')
      .in('booking_service_id', bsIds);
    for (const row of addonRows || []) {
      const id = row.booking_service_id as string;
      addonByBsId.set(id, (addonByBsId.get(id) || 0) + (Number(row.price) || 0));
    }
  }

  const serviceIds = [...new Set(bookingServices.map((bs) => bs.service_id))];

  const { data: additionalTaxes, error: atError } = await supabase
    .from('service_additional_taxes')
    .select('service_id, name, rate')
    .in('service_id', serviceIds);

  if (atError || !additionalTaxes || additionalTaxes.length === 0) return [];

  const taxMap = new Map<string, { name: string; rate: number; amount: number; taxableBase: number }>();

  for (const tax of additionalTaxes) {
    const lines = bookingServices.filter((bs) => bs.service_id === tax.service_id);
    const serviceScaledGross = lines.reduce((sum, bs) => {
      const lineGross = (Number(bs.price) || 0) + (addonByBsId.get(bs.id) || 0);
      return sum + lineGross * lineMultiplier;
    }, 0);
    const taxAmount = (serviceScaledGross * tax.rate) / 100;

    const key = `${tax.name}_${tax.rate}`;
    if (taxMap.has(key)) {
      const existing = taxMap.get(key)!;
      existing.amount += taxAmount;
      existing.taxableBase += serviceScaledGross;
    } else {
      taxMap.set(key, {
        name: tax.name,
        rate: tax.rate,
        amount: taxAmount,
        taxableBase: serviceScaledGross,
      });
    }
  }

  return Array.from(taxMap.values()).map((t) => ({
    ...t,
    amount: Math.round(t.amount * 100) / 100,
    taxableBase: Math.round(t.taxableBase * 100) / 100,
  }));
}